import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export function LanguageSwitcher() {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const changeLanguage = (lng: string) => {
    // Explicitly save to localStorage first to ensure persistence
    localStorage.setItem('i18nextLng', lng);
    i18n.changeLanguage(lng);
    // Force reload to ensure all components re-render with new translations
    window.location.reload();
  };

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'ar', name: 'العربية' }
  ];

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  const currentLanguage = languages.find(l => l.code === i18n.language) || languages[0];

  return (
    <div className="p-2" ref={containerRef}>
      {/* Main trigger button */}
      <Button
        variant="ghost"
        className="w-full justify-start text-gray-600 hover:bg-[#F3F4F6] hover:text-gray-900 px-5 py-3"
        onClick={() => setIsOpen(!isOpen)}
        type="button"
      >
        <Globe className="h-5 w-5 me-4" />
        <span className="flex-1 text-start">{t('dashboard.language')}</span>
        <ChevronDown className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")} />
      </Button>

      {/* Language options - inline expandable for mobile-friendly UX */}
      {isOpen && (
        <div className="mt-1 ms-9 space-y-1 animate-in slide-in-from-top-2 duration-150">
          {languages.map(({ code, name }) => (
            <button
              key={code}
              onClick={() => changeLanguage(code)}
              className={cn(
                "w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors",
                "hover:bg-gray-100 active:bg-gray-200",
                i18n.language === code
                  ? "bg-gray-100 text-gray-900 font-medium"
                  : "text-gray-600"
              )}
              type="button"
            >
              {i18n.language === code && <Check className="h-4 w-4 text-green-600" />}
              <span className={cn(i18n.language !== code && "ms-6")}>{name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
} 